import { useEffect, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

/**
 * Looping recreation of Stark Crawl's project coverage UI for the landing
 * page: discovery proposes hosts, they move to Accepted, then prospects /
 * captures climb and category coverage flips to pass.
 *
 * Recreation (not a recording) so it stays sharp at any width and can't drift
 * into a UI we don't ship. Server render matches the opening frame.
 */

type StepType =
  'accepting' | 'coverage' | 'crawling' | 'discovering' | 'done' | 'proposing';

const HOLD_MS: Record<StepType, number> = {
  discovering: 900,
  proposing: 1400,
  accepting: 900,
  crawling: 2200,
  coverage: 1600,
  done: 3200,
};

const FIRST_PASS_HOLD_MS: Partial<Record<StepType, number>> = {
  discovering: 400,
  proposing: 900,
};

const NEXT: Record<StepType, StepType> = {
  discovering: 'proposing',
  proposing: 'accepting',
  accepting: 'crawling',
  crawling: 'coverage',
  coverage: 'done',
  done: 'discovering',
};

const SEED_HOSTS = [
  { domain: 'feinkost-mueller.de', name: 'Feinkost Müller' },
  { domain: 'bio-marktplatz.at', name: 'Bio Marktplatz' },
  { domain: 'specialtyfoods.nl', name: 'Specialty Foods NL' },
  { domain: 'gourmet-direct.fr', name: 'Gourmet Direct' },
  { domain: 'delikatessen.ch', name: 'Delikatessen CH' },
  { domain: 'artisanal-foods.be', name: 'Artisanal Foods' },
] as const;

const CATEGORIES = [
  { name: 'Cheese & dairy', minHosts: 3 },
  { name: 'Oils & vinegars', minHosts: 2 },
  { name: 'Charcuterie', minHosts: 3 },
  { name: 'Confectionery', minHosts: 2 },
] as const;

const TARGET_PROSPECTS = [18, 12, 9, 14, 7, 11] as const;
const TARGET_CAPTURES = [11, 8, 5, 9, 4, 6] as const;

const prospectTarget = (index: number): number => TARGET_PROSPECTS[index] ?? 0;
const captureTarget = (index: number): number => TARGET_CAPTURES[index] ?? 0;
const categoryAt = (index: number) => CATEGORIES[index] ?? CATEGORIES[0];

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

export function CoverageDemo() {
  const [step, setStep] = useState<StepType>('discovering');
  const [proposedCount, setProposedCount] = useState(0);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [crawlProgress, setCrawlProgress] = useState(0);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setStep('done');

      setProposedCount(SEED_HOSTS.length);

      setAcceptedCount(SEED_HOSTS.length);

      setCrawlProgress(1);

      return;
    }

    let isStopped = false;
    let isFirstPass = true;

    const wait = (ms: number, fn: () => void) => {
      const id = setTimeout(() => {
        if (!isStopped) fn();
      }, ms);

      timers.current.push(id);
    };

    const run = (current: StepType) => {
      if (isStopped) return;

      setStep(current);

      if (current === 'discovering') {
        setProposedCount(0);

        setAcceptedCount(0);

        setCrawlProgress(0);
      }

      if (current === 'done') isFirstPass = false;

      if (current === 'proposing') {
        SEED_HOSTS.forEach((_, index) => {
          wait(180 + index * 220, () => setProposedCount(index + 1));
        });

        wait(
          180 + SEED_HOSTS.length * 220 + 400,
          () => run('accepting')
        );

        return;
      }

      if (current === 'accepting') {
        wait(350, () => {
          setAcceptedCount(SEED_HOSTS.length);

          setProposedCount(0);
        });

        wait(HOLD_MS.accepting, () => run('crawling'));

        return;
      }

      if (current === 'crawling') {
        const ticks = 12;
        for (let i = 1; i <= ticks; i++) {
          wait(i * 140, () => setCrawlProgress(i / ticks));
        }

        wait(ticks * 140 + 350, () => run('coverage'));

        return;
      }

      const hold = isFirstPass
        ? FIRST_PASS_HOLD_MS[current] ?? HOLD_MS[current]
        : HOLD_MS[current];

      wait(hold, () => run(NEXT[current]));
    };

    run('discovering');

    return () => {
      isStopped = true;

      timers.current.forEach(clearTimeout);

      timers.current = [];
    };
  }, []);

  const tab: 'accepted' | 'pending' =
    step === 'discovering' || step === 'proposing' ? 'pending' : 'accepted';

  const uniqueNodes = Math.round(
    TARGET_PROSPECTS.reduce((sum, n) => sum + n, 0) * crawlProgress
  );

  const hostCount =
    step === 'discovering' || step === 'proposing'
      ? proposedCount
      : acceptedCount;

  const statusLabel =
    step === 'discovering'
      ? 'Discovery running…'
      : step === 'proposing'
        ? `${proposedCount} proposed hosts`
        : step === 'accepting'
          ? 'Accepting hosts…'
          : step === 'crawling'
            ? 'Crawl & extract running…'
            : `${acceptedCount} accepted hosts · ${uniqueNodes} unique nodes`;

  return (
    <div
      role="img"
      aria-label="A demonstration of Stark Crawl: hosts are discovered from a directory, accepted into a project, then coverage climbs as prospects and captures fill in."
      className="relative h-[28rem] w-full bg-background-primary text-left sm:h-[32rem] lg:h-[36rem]"
    >
      <div aria-hidden="true" className="flex h-full min-h-0">
        {/* ─── Side nav (matches project chrome) ─────────────────────────── */}
        <aside className="hidden w-36 shrink-0 flex-col gap-1 border-r border-grey-t-800 px-3 py-4 sm:flex">
          <p className="mb-3 truncate px-2 font-grotesque text-sm font-semibold text-white">
            Food types DE
          </p>
          {(
            [
              ['Hosts', true],
              ['Topics', false],
              ['Segments', false],
              ['Workflows', false],
            ] as const
          ).map(([label, active]) => (
            <span
              key={label}
              className={`rounded-xs px-2 py-1.5 text-sm ${
                active
                  ? 'bg-primary-400/10 text-primary-400'
                  : 'text-text-low-priority'
              }`}
            >
              {label}
            </span>
          ))}
        </aside>

        {/* ─── Main ──────────────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col px-4 py-4 sm:px-6">
          <div className="mb-3 flex items-end justify-between gap-3 border-b border-grey-t-800">
            <div className="flex gap-4">
              {(['pending', 'accepted'] as const).map((t) => (
                <span
                  key={t}
                  className={`pb-2 text-sm capitalize ${
                    tab === t
                      ? 'border-b-2 border-primary-400 text-primary-400'
                      : 'text-text-low-priority'
                  }`}
                >
                  {t}
                </span>
              ))}
            </div>
            <span className="pb-2 font-open text-xs text-text-low-priority sm:text-sm">
              {statusLabel}
            </span>
          </div>

          {/* Host rows */}
          <div className="min-h-0 flex-1 overflow-hidden">
            <div className="mb-2 grid grid-cols-[1fr_auto] gap-3 px-1 text-xs text-text-low-priority sm:grid-cols-[1.2fr_1fr_auto]">
              <span>Host</span>
              <span className="hidden sm:inline">Domain</span>
              <span className="text-right tabular-nums">
                Captures / prospects
              </span>
            </div>
            <ul className="flex flex-col gap-1.5">
              {SEED_HOSTS.map((host, index) => {
                const visiblePending =
                  tab === 'pending' && index < proposedCount;

                const visibleAccepted =
                  tab === 'accepted' && index < acceptedCount;

                const visible = visiblePending || visibleAccepted;
                const prospects = Math.round(
                  prospectTarget(index) * crawlProgress
                );

                const captures = Math.round(
                  captureTarget(index) * crawlProgress
                );

                return (
                  <li
                    key={host.domain}
                    className={`grid grid-cols-[1fr_auto] items-center gap-3 rounded-xs border border-grey-t-800 bg-background-tertiary px-3 py-2 transition-all duration-300 sm:grid-cols-[1.2fr_1fr_auto] ${
                      visible
                        ? 'translate-y-0 opacity-100'
                        : 'pointer-events-none -translate-y-1 opacity-0'
                    }`}
                    style={{
                      transitionDelay: visible ? `${index * 40}ms` : '0ms',
                    }}
                  >
                    <span className="truncate font-open text-sm text-white">
                      {host.name}
                    </span>
                    <span className="hidden truncate font-mono text-xs text-text-low-priority sm:inline">
                      {host.domain}
                    </span>
                    <span className="text-right font-open text-sm tabular-nums text-grey-200">
                      {tab === 'accepted' ? (
                        <span
                          className={
                            crawlProgress > 0
                              ? 'text-primary-400'
                              : 'text-text-low-priority'
                          }
                        >
                          ({captures}/{prospects})
                        </span>
                      ) : (
                        <span className="text-text-low-priority">proposed</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>

            {step === 'discovering' && (
              <div className="mt-6 flex items-center gap-2 text-sm text-grey-300">
                <span className="flex gap-0.5">
                  {[0, 1, 2, 3].map((dot) => (
                    <span
                      key={dot}
                      className="size-0.5 animate-pulse rounded-full bg-grey-400"
                      style={{ animationDelay: `${dot * 140}ms` }}
                    />
                  ))}
                </span>
                Discovering hosts from directories…
              </div>
            )}
          </div>

          {/* Coverage strip — appears once crawl/coverage starts */}
          <div
            className={`mt-3 shrink-0 overflow-hidden rounded-xs border border-grey-t-800 bg-background-secondary transition-all duration-500 ${
              step === 'crawling' ||
              step === 'coverage' ||
              step === 'done'
                ? 'max-h-40 opacity-100'
                : 'max-h-0 border-transparent opacity-0'
            }`}
          >
            <div className="space-y-3 p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-grotesque text-sm font-semibold text-white">
                  Segment coverage
                </p>
                <p className="font-open text-xs text-text-low-priority sm:text-sm">
                  {hostCount} hosts · {uniqueNodes} unique nodes ·{' '}
                  <span className="text-primary-400">
                    {
                      CATEGORIES.filter((_, i) => {
                        const hostsWithData = SEED_HOSTS.filter(
                          (___, hi) =>
                            Math.round(
                              prospectTarget(hi) * crawlProgress
                            ) > 0
                        ).length;

                        return hostsWithData >= categoryAt(i).minHosts;
                      }).length
                    }{' '}
                    pass
                  </span>
                  {' / '}
                  {CATEGORIES.length} categories
                </p>
              </div>

              <div className="h-1.5 overflow-hidden rounded-full bg-grey-t-800">
                <div
                  className="h-full rounded-full bg-primary-400 transition-[width] duration-300 ease-out"
                  style={{
                    width: `${Math.round(crawlProgress * 100)}%`,
                  }}
                />
              </div>

              <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
                {CATEGORIES.map((cat, index) => {
                  const hostsWithData = clamp(
                    Math.round(
                      (SEED_HOSTS.length * crawlProgress * (index + 2)) /
                        (CATEGORIES.length + 1)
                    ),
                    0,
                    SEED_HOSTS.length
                  );

                  const passed =
                    crawlProgress > 0.55 &&
                    hostsWithData >= cat.minHosts;

                  return (
                    <li
                      key={cat.name}
                      className="flex items-center gap-1.5 font-open text-xs text-grey-200"
                    >
                      <span
                        className={`flex size-3.5 shrink-0 items-center justify-center rounded-full ${
                          passed
                            ? 'bg-primary-400/20 text-primary-400'
                            : 'bg-grey-t-800 text-text-low-priority'
                        }`}
                      >
                        {passed ? (
                          <CheckIcon className="size-2.5" strokeWidth={3} />
                        ) : null}
                      </span>
                      <span className="truncate">{cat.name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
